<template>
    <b-overlay :show="loading" variant="white" :rounded="true">
        <template v-slot:overlay>
            <div class="text-center">
                <b-spinner
                    label="Carregando..."
                    style="width: 3rem; height: 3rem"
                />
            </div>
        </template>
        <b-card class="p-0 m-0 shadow-sm" body-class="p-2">
            <chart-toolbar :chart="chart" />
            <div style="position: relative; height: 500px">
                <canvas ref="canvas" />
            </div>
        </b-card>
    </b-overlay>
</template>

<script>
import { Chart, defaultOptions, tickOptions } from "@/modules/chart.js";
import ChartToolbar from "@/modules/common/components/ChartToolbar";
import hasProperty from "@/utils/hasProperty";
import { SensorReadingKind } from "@/modules/common/settings";
import { ESensor } from "../../../store/sensors";

export default {
    name: "SensorChart",
    components: {
        ChartToolbar
    },
    props: {
        loading: {
            type: Boolean
        },
        settings: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            chart: null
        };
    },
    mounted() {
        this.chart = new Chart(this.$refs.canvas, {
            type: "line",
            data: {
                datasets: []
            },
            options: {
                ...defaultOptions,
                scales: {
                    xAxes: [
                        {
                            display: "auto",
                            type: "time",
                            time: {
                                tooltipFormat: "DD/MM/YYYY HH:mm",
                                displayFormats: {
                                    hour: "D MMM HH:mm",
                                    day: "D MMM"
                                },
                                minUnit: "hour"
                            },
                            ticks: {
                                maxTicksLimit: 9
                            }
                        }
                    ],
                    yAxes: [
                        {
                            type: "linear",
                            id: "left-axis-0",
                            display: "auto",
                            scaleLabel: {
                                display: true,
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        },
                        {
                            type: "linear",
                            id: "left-axis-1",
                            display: "auto",
                            scaleLabel: {
                                display: true,
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        },
                        {
                            type: "linear",
                            id: "right-axis-0",
                            display: "auto",
                            position: "right",
                            scaleLabel: {
                                display: true,
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        },
                        {
                            type: "linear",
                            id: "right-axis-1",
                            display: "auto",
                            position: "right",
                            scaleLabel: {
                                display: true,
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        }
                    ]
                }
            }
        });
    },
    beforeDestroy() {
        this.chart?.destroy();
    },
    methods: {
        update(
            { includeTemperature, sensorKind, includeQuotaFlux },
            sensors,
            reservoirInfos
        ) {
            this.chart.resetZoom();

            const includeResult = hasProperty(
                this.settings.SENSOR_UNITS[sensorKind],
                SensorReadingKind.RESULT
            );

            const includeReading = hasProperty(
                this.settings.SENSOR_UNITS[sensorKind],
                SensorReadingKind.READING
            );

            const numSensors = sensors.length;

            const readingData = {
                unit: includeReading
                    ? this.settings.SENSOR_UNITS[sensorKind][
                          SensorReadingKind.READING
                      ]
                    : "",
                series: new Array(includeReading ? numSensors : 0)
            };
            const quotaFluxData = {
                unit: "m",
                serie: {}
            };
            const resultData = {
                unit: includeResult
                    ? this.settings.SENSOR_UNITS[sensorKind][
                          SensorReadingKind.RESULT
                      ]
                    : "",
                series: new Array(includeResult ? numSensors : 0)
            };
            const temperatureData = {
                unit: includeTemperature
                    ? this.settings.SENSOR_UNITS[sensorKind][
                          SensorReadingKind.TEMPERATURE
                      ]
                    : "",
                series: new Array(includeTemperature ? numSensors : 0)
            };

            if (includeQuotaFlux) {
                quotaFluxData.serie = {
                    label: `Cota montante`,
                    data: reservoirInfos.map(item => ({
                        x: item.epoch,
                        y: item.quota_flux
                    })),
                    yAxisID: "left-axis-1",
                    fill: false,
                    pointRadius: 0,
                    lineTension: 0
                };
            }

            for (let i = 0; i < numSensors; i++) {
                const { name, readings: points } = sensors[i];

                if (includeReading) {
                    readingData.series[i] = {
                        label: `${name} (${this.settings.SENSOR_LABELS[
                            sensorKind
                        ][SensorReadingKind.READING].toLowerCase()})`,
                        data: points
                            .filter(
                                item => item.kind === SensorReadingKind.READING
                            )
                            .map(item => ({ x: item.epoch, y: item.value })),
                        yAxisID: "left-axis-0",
                        fill: false,
                        pointRadius: 0,
                        lineTension: 0
                    };
                }

                if (includeResult) {
                    resultData.series[i] = {
                        label: `${name} (${this.settings.SENSOR_LABELS[
                            sensorKind
                        ][SensorReadingKind.RESULT].toLowerCase()})`,
                        data: points
                            .filter(
                                item => item.kind === SensorReadingKind.RESULT
                            )
                            .map(item => ({ x: item.epoch, y: item.value })),
                        yAxisID: "right-axis-0",
                        fill: false,
                        pointRadius: 0,
                        lineTension: 0
                    };
                }

                if (includeTemperature) {
                    temperatureData.series[i] = {
                        label: `${name} (${this.settings.SENSOR_LABELS[
                            sensorKind
                        ][SensorReadingKind.TEMPERATURE].toLowerCase()})`,
                        data: points
                            .filter(
                                item =>
                                    item.kind === SensorReadingKind.TEMPERATURE
                            )
                            .map(item => ({ x: item.epoch, y: item.value })),
                        yAxisID: "right-axis-1",
                        fill: false,
                        pointRadius: 0,
                        lineTension: 0
                    };
                }
            }

            if (includeReading) {
                this.chart.options.scales.yAxes[0].scaleLabel.labelString = `${
                    this.settings.SENSOR_LABELS[sensorKind][
                        SensorReadingKind.READING
                    ]
                } (${readingData.unit})`;
                this.chart.options.scales.yAxes[0].display = true;
            } else {
                this.chart.options.scales.yAxes[0].display = false;
            }

            let datasets = readingData.series;

            if (includeResult) {
                this.chart.options.scales.yAxes[2].scaleLabel.labelString = `${
                    this.settings.SENSOR_LABELS[sensorKind][
                        SensorReadingKind.RESULT
                    ]
                } (${resultData.unit})`;

                datasets = datasets.concat(resultData.series);
                this.chart.options.scales.yAxes[2].display = true;
            } else {
                this.chart.options.scales.yAxes[2].display = false;
            }

            if (includeTemperature) {
                this.chart.options.scales.yAxes[3].scaleLabel.labelString = `${
                    this.settings.SENSOR_LABELS[sensorKind][
                        SensorReadingKind.TEMPERATURE
                    ]
                } (${temperatureData.unit})`;
                datasets = datasets.concat(temperatureData.series);
                this.chart.options.scales.yAxes[3].display = true;
            } else {
                this.chart.options.scales.yAxes[3].display = false;
            }

            if (includeQuotaFlux) {
                this.chart.options.scales.yAxes[1].scaleLabel.labelString = `Cota (${quotaFluxData.unit})`;

                datasets.push(quotaFluxData.serie);
                this.chart.options.scales.yAxes[1].display = true;
            } else {
                this.chart.options.scales.yAxes[1].display = false;
            }

            this.chart.data.datasets = datasets;
            this.chart.update();
        }
    }
};
</script>
